import { api } from 'api';
import SearchIcon from 'assets/ver2/icons/search-icon-2.svg';
import { preventKeyDownEnter } from 'components/common/func';
import { ADDRESS_TRIM_REGEX } from 'constants';
import { DEBOUNCE_SEC, ENTER_KEY_DOWN } from 'constants/common';
import {
  PII_PROFILE_RESIDENTIAL_ADDRESS_POST_CODE_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_STATE_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_SUBURB_NAME,
} from 'features/pii/const';
import { Fade } from 'helpers/animation';
import { checkMaintenance } from 'helpers/maintenance';
import useFormInput from 'hooks/form/useFormInput';
import useCommonStore from 'hooks/useCommonStore';
import useDebounceCallback from 'hooks/useDebounceCallback';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import Form from 'react-bootstrap/Form';
import styled from 'styled-components';
import { cursorPointerMixin, paragraph500Mixin } from 'styles';

const FormInputAddressPostcodeValidation = ({
  field,
  information,
  setInformation,
  validation,
  setValidation,
  postcodeSuggestion,
  setPostcodeSuggestion,
}) => {
  const { t } = useLang();
  const { isLoading, setLoading } = useCommonStore();

  const {
    inputRef,
    resetFormInput,
    setInformationFormInput,
    getValueTrimAll,
    FormLabel,
    FormControlFeedback,
  } = useFormInput(field);

  const searchValue =
    information[PII_PROFILE_RESIDENTIAL_ADDRESS_POST_CODE_NAME];

  const fetchLookupPostcodeSuggestion = async (value) => {
    try {
      const { data } = await api.lookupPostcodes(
        value,
        'd6fe8ff05352dfff3c3a3367d7140364'
      );
      console.log(
        `⭐ FormInputAddressPostcodeValidation 👉 fetchLookupPostcodeSuggestion ➡️ data ↩`,
        data
      );
      if (data?.payload) {
        setPostcodeSuggestion(data.payload);
      }
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 FormInputAddressPostcodeValidation ❌ fetchLookupPostcodeSuggestion ➡️ ERROR ↩`,
        error?.message
      );
    } finally {
      setLoading(false);
    }
  };
  const checkLookupPostcodeSuggestion = async (value) => {
    if (!value) return;
    try {
      fetchLookupPostcodeSuggestion(value);
    } catch (error) {
      console.error(
        `🔥 FormInputAddressPostcodeValidation ❌ checkLookupPostcodeSuggestion ➡️ ERROR ↩`,
        error?.message
      );
    }
  };
  const { debounce: checkLookupPostcodeSuggestionDebounce } =
    useDebounceCallback((value) => {
      checkLookupPostcodeSuggestion(value);
    }, DEBOUNCE_SEC);

  const onReset = () => {
    resetFormInput({
      information: {
        ...information,
        [PII_PROFILE_RESIDENTIAL_ADDRESS_SUBURB_NAME]: '',
        [PII_PROFILE_RESIDENTIAL_ADDRESS_STATE_NAME]: '',
      },
      setInformation,
      validation,
      setValidation,
    });
    setPostcodeSuggestion(null);
  };

  const handleOnClickSearchIconButton = () => {
    if (isEmpty(searchValue)) return;
    if (isLoading) return;
    if (!isEmpty(postcodeSuggestion)) return;
    setLoading(true);
    checkLookupPostcodeSuggestion(searchValue);
  };

  const handleOnChangeFormInputAddressPostcode = (e) => {
    const { value } = e.target;

    if (!value) {
      onReset();
      return;
    }
    setInformationFormInput({
      value,
      information,
      setInformation,
    });

    checkLookupPostcodeSuggestionDebounce(value);
  };
  const handleOnBlurFormInputAddressPostcode = (e) => {
    const { value } = e.target;

    if (!value) return;
    setInformationFormInput({
      value: getValueTrimAll(value, true, ADDRESS_TRIM_REGEX),
      information,
      setInformation,
    });
  };
  const handleOnKeyDownFormInputAddressPostcode = (e) => {
    if (isEmpty(searchValue)) return;
    const { key } = e;
    if (key === ENTER_KEY_DOWN) {
      handleOnClickSearchIconButton();
    }
  };

  return (
    <FormGroupStyled onKeyDown={preventKeyDownEnter}>
      <FormLabel styles={{ marginBottom: '4px' }} />
      <FormControl
        ref={inputRef}
        type={field.type}
        name={field.name}
        value={information[field.name] || ''}
        defaultValue={information[field.name]}
        placeholder={field.placeholder ? t(field.placeholder) : ''}
        isInvalid={validation[field.name]}
        onChange={handleOnChangeFormInputAddressPostcode}
        onBlur={handleOnBlurFormInputAddressPostcode}
        // onKeyDown={handleOnKeyDownFormInputAddressPostcode}
      />
      <SearchIconButton
        src={SearchIcon}
        height={20}
        width={20}
        // onClick={handleOnClickSearchIconButton}
      />
      <FormControlFeedback
        value={t(validation[field.name])}
        validationStyles={{ position: 'unset' }}
      />
    </FormGroupStyled>
  );
};

const FormGroupStyled = styled(Form.Group)`
  position: relative;
  ${Fade};
`;

const FormControl = styled(Form.Control)`
  ${paragraph500Mixin};
  text-transform: uppercase;
  height: 44px;
  border: 1px solid var(--ds-c-grey-light);
  border-radius: 8px;
  padding-inline: 42px 10px;
  background: var(--ds-c-white);

  &:focus {
    border: 1px solid var(--ds-c-grey-dark) !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus,
  &.is-invalid {
    border: 1px solid var(--ds-c-red) !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
  }

  &[readonly] {
    outline: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
  }

  &::placeholder {
    ${paragraph500Mixin};
    text-transform: uppercase;
    color: var(--ds-c-blue-disabled) !important;
  }
`;

const SearchIconButton = styled.img`
  ${cursorPointerMixin};
  position: absolute;
  top: 36px;
  left: 12px;
`;

export default FormInputAddressPostcodeValidation;
